import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { BluffPlayersRoster } from "../../party/BluffPlayersRoster";
import { parseBluffGamePayload, type BluffGamePayload } from "../../party/answerBluffGamePayload";
import { bluffQaCombinedPhase } from "../../party/answerBluffPhaseHelpers";
import {
  applyPresenceToRoster,
  parseBluffPresencePayload,
  parseBluffSocketReadyPayload,
  type BluffRoster,
} from "../../party/answerBluffPresence";
import {
  ANSWER_BLUFF_WS_PING_INTERVAL_MS,
  parseAnswerBluffIncoming,
  stringifyBluffWsPing,
  stringifyJoin,
  stringifyVote,
  type AnswerBluffGuestPhase,
} from "../../party/answerBluffRoomMessages";
import { bluffVoteOptionsShuffleSeed, shuffleBluffVoteOptionsDeterministic } from "../../party/answerBluffVoteShuffle";
import { answerBluffWsUrl } from "../../party/answerBluffWsUrl";
import {
  BluffLeaderboardList,
  BluffQuestionCard,
  BluffScoresStrip,
  bluffPhaseEmojiLabel,
  bluffVoteOptionButtonSx,
} from "../../party/bluffGameUi";
import {
  bluffAnswerRejectReasonMessage,
  bluffSubmissionMatchesTruth,
  resolveBluffCanonicalTruth,
} from "../../party/bluffTruthAnswer";
import { partyBluffAnswerFieldSx, partyOutlinedFieldSx } from "../../party/partyFieldSx";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import "../../party/partyShell.css";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { useBluffPhaseCountdown } from "../../party/useBluffPhaseCountdown";
import type { PartyShellPalette } from "../../theme/theme";
import { MixpanelEvents, trackEvent } from "../../utils/mixpanel";

const initialPhase: AnswerBluffGuestPhase = "idle";

export default function PartyJoinBluffPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const fieldSx = useMemo(() => partyOutlinedFieldSx(partyPal), [partyPal]);
  const answerFieldSx = useMemo(() => partyBluffAnswerFieldSx(partyPal), [partyPal]);
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code")?.trim() ?? "";
  const wsPath = searchParams.get("ws")?.trim() ?? "";

  const [displayName, setDisplayName] = useState("");
  const [connected, setConnected] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [phase, setPhase] = useState<AnswerBluffGuestPhase>(initialPhase);
  const [round, setRound] = useState(0);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [voteSentForRound, setVoteSentForRound] = useState<number | null>(null);
  const [playerRoster, setPlayerRoster] = useState<BluffRoster>({});
  const [myConnectionId, setMyConnectionId] = useState<string | null>(null);
  const [serverGame, setServerGame] = useState<BluffGamePayload | null>(null);
  const [answerDraft, setAnswerDraft] = useState("");
  const [voteSubmitted, setVoteSubmitted] = useState(false);
  const [answerAck, setAnswerAck] = useState<string | null>(null);
  const [answerSubmitLocked, setAnswerSubmitLocked] = useState(false);
  const [answerTruthBlockHint, setAnswerTruthBlockHint] = useState<string | null>(null);
  /** Bumps when join is sent so auto-"-" effect re-runs (joinSentRef alone does not re-render). */
  const [joinSentEpoch, setJoinSentEpoch] = useState(0);

  const wsRef = useRef<WebSocket | null>(null);
  const bluffWsPingIntervalRef = useRef<number | null>(null);
  const guestReconnectTimerRef = useRef<number | null>(null);
  /** True after explicit disconnect / unmount — skip auto-reconnect on socket drop. */
  const guestStopReconnectRef = useRef(false);
  /** True once this tab had an open join session (background reconnect only). */
  const guestHadJoinedSessionRef = useRef(false);
  const joinSentRef = useRef(false);
  const joinFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoDashSentForDeadlineRef = useRef<string | null>(null);
  const lastAnswerWindowKeyRef = useRef<string>("");
  /** Avoid auto "-": require seeing `phaseSecondsLeft > 1` in this QA window (aligned with host). */
  const answerPhaseSawPositiveCountdownRef = useRef(false);
  const serverGameRef = useRef<BluffGamePayload | null>(null);
  const rtl = i18n.language === "ar";
  const shareModeTitle = usePartyShareModeTitle("bluff", rtl ? "إجابة أو خدعة" : "Answer or Bluff");

  useEffect(() => {
    serverGameRef.current = serverGame;
  }, [serverGame]);

  const clearBluffWsPing = useCallback(() => {
    if (bluffWsPingIntervalRef.current != null) {
      window.clearInterval(bluffWsPingIntervalRef.current);
      bluffWsPingIntervalRef.current = null;
    }
  }, []);

  const missing = !code || !wsPath;

  useEffect(() => {
    trackEvent(MixpanelEvents.PARTY_BLUFF_JOIN_PAGE_VIEWED, {
      missing_params: missing,
      has_code_param: Boolean(code),
      has_ws_param: Boolean(wsPath),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- entry screen once per mount
  }, []);

  const rosterForUi: BluffRoster =
    serverGame && Object.keys(serverGame.players).length > 0 ? serverGame.players : playerRoster;

  const disconnect = useCallback((opts?: { userLeaving?: boolean }) => {
    if (opts?.userLeaving) {
      guestStopReconnectRef.current = true;
      guestHadJoinedSessionRef.current = false;
    }
    if (guestReconnectTimerRef.current != null) {
      window.clearTimeout(guestReconnectTimerRef.current);
      guestReconnectTimerRef.current = null;
    }
    clearBluffWsPing();
    if (joinFallbackRef.current) {
      clearTimeout(joinFallbackRef.current);
      joinFallbackRef.current = null;
    }
    joinSentRef.current = false;
    setJoinSentEpoch(0);
    setMyConnectionId(null);
    try {
      const w = wsRef.current;
      if (w) {
        w.onclose = null;
        w.close();
      }
    } catch {
      /* ignore */
    }
    wsRef.current = null;
    setConnected(false);
    setPlayerRoster({});
    setServerGame(null);
    setAnswerDraft("");
    setVoteSubmitted(false);
    setAnswerAck(null);
    setAnswerSubmitLocked(false);
    setAnswerTruthBlockHint(null);
    autoDashSentForDeadlineRef.current = null;
    lastAnswerWindowKeyRef.current = "";
    answerPhaseSawPositiveCountdownRef.current = false;
  }, [clearBluffWsPing]);

  useEffect(() => () => disconnect({ userLeaving: true }), [disconnect]);

  useEffect(() => {
    if (!serverGame) return;
    if (bluffQaCombinedPhase(serverGame.phase)) {
      const windowKey = `${serverGame.round_index}-${serverGame.phase_ends_at ?? ""}`;
      if (lastAnswerWindowKeyRef.current !== windowKey) {
        lastAnswerWindowKeyRef.current = windowKey;
        setVoteSubmitted(false);
        setAnswerAck(null);
        setAnswerSubmitLocked(false);
        setAnswerTruthBlockHint(null);
        autoDashSentForDeadlineRef.current = null;
        answerPhaseSawPositiveCountdownRef.current = false;
      }
    } else {
      lastAnswerWindowKeyRef.current = "";
      answerPhaseSawPositiveCountdownRef.current = false;
    }
    if (serverGame.phase === "vote") {
      setVoteSubmitted(false);
    }
  }, [serverGame]);

  const phaseSecondsLeft = useBluffPhaseCountdown(serverGame?.phase_ends_at);

  /** Same as host: require >1 so a lone tick of `1` (stale or join-at-buzzer) does not arm auto "-". */
  useEffect(() => {
    if (!serverGame || !bluffQaCombinedPhase(serverGame.phase)) return;
    if (phaseSecondsLeft != null && phaseSecondsLeft > 1) {
      answerPhaseSawPositiveCountdownRef.current = true;
    }
  }, [serverGame, phaseSecondsLeft]);

  useEffect(() => {
    if (!connected || !joinSentRef.current || joinSentEpoch < 1 || !serverGame || !bluffQaCombinedPhase(serverGame.phase)) return;
    if (!serverGame.phase_ends_at || phaseSecondsLeft == null || phaseSecondsLeft > 1) return;
    if (phaseSecondsLeft !== 1) return;
    if (!answerPhaseSawPositiveCountdownRef.current) return;
    if (answerSubmitLocked) return;
    if (answerDraft.trim()) return;
    if (autoDashSentForDeadlineRef.current === serverGame.phase_ends_at) return;
    autoDashSentForDeadlineRef.current = serverGame.phase_ends_at;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "bluff_answer", text: "-" }));
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_ANSWER_AUTO_SUBMITTED, {
        round_index: serverGame.round_index,
      });
    } catch {
      setConnectError(t("party.joinBluff.errorConnect"));
    }
  }, [connected, joinSentEpoch, serverGame, phaseSecondsLeft, answerSubmitLocked, answerDraft, t]);

  const connect = useCallback(() => {
    if (missing || !displayName.trim()) return;
    guestStopReconnectRef.current = false;
    trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_CONNECT_ATTEMPTED, {
      display_name_length: displayName.trim().length,
    });
    disconnect();
    setConnectError(null);
    setVoteSentForRound(null);
    setPhase(initialPhase);
    setRound(0);
    setQuestion("");
    setAnswer("");
    setPlayerRoster({});
    setServerGame(null);
    setAnswerDraft("");
    setVoteSubmitted(false);
    setAnswerAck(null);
    setAnswerSubmitLocked(false);
    setAnswerTruthBlockHint(null);
    joinSentRef.current = false;
    setJoinSentEpoch(0);
    setMyConnectionId(null);
    if (joinFallbackRef.current) {
      clearTimeout(joinFallbackRef.current);
      joinFallbackRef.current = null;
    }

    let ws: WebSocket;
    try {
      ws = new WebSocket(answerBluffWsUrl(wsPath));
    } catch {
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_WS_ERROR, { stage: "constructor" });
      setConnectError(t("party.joinBluff.errorConnect"));
      return;
    }

    wsRef.current = ws;

    const sendJoinOnce = () => {
      if (joinSentRef.current || ws.readyState !== WebSocket.OPEN) return;
      joinSentRef.current = true;
      setJoinSentEpoch((n) => n + 1);
      if (joinFallbackRef.current) {
        clearTimeout(joinFallbackRef.current);
        joinFallbackRef.current = null;
      }
      try {
        ws.send(stringifyJoin(displayName));
        trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_JOIN_SENT, {});
      } catch {
        setConnectError(t("party.joinBluff.errorConnect"));
      }
    };

    ws.onopen = () => {
      guestHadJoinedSessionRef.current = true;
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_WS_OPENED, {});
      setConnected(true);
      clearBluffWsPing();
      bluffWsPingIntervalRef.current = window.setInterval(() => {
        const w = wsRef.current;
        if (!w || w.readyState !== WebSocket.OPEN) return;
        try {
          w.send(stringifyBluffWsPing());
        } catch {
          /* ignore */
        }
      }, ANSWER_BLUFF_WS_PING_INTERVAL_MS);
      joinFallbackRef.current = setTimeout(() => {
        joinFallbackRef.current = null;
        sendJoinOnce();
      }, 1200);
    };

    ws.onmessage = (ev) => {
      const raw = String(ev.data);
      let data: unknown;
      try {
        data = JSON.parse(raw);
      } catch {
        return;
      }
      if (data && typeof data === "object" && (data as { type?: unknown }).type === "ack") {
        const o = data as Record<string, unknown>;
        if (o.event === "answer_rejected" || o.ok === false) {
          setAnswerSubmitLocked(false);
          const reason = typeof o.reason === "string" ? o.reason : undefined;
          const msg = bluffAnswerRejectReasonMessage(reason, t);
          if (reason === "cannot_submit_truth") {
            setAnswerTruthBlockHint(msg);
            setAnswerAck(null);
          } else {
            setAnswerTruthBlockHint(null);
            setAnswerAck(msg);
          }
        } else if (o.event === "answer_recorded") {
          setAnswerDraft("");
          setAnswerSubmitLocked(true);
          setAnswerTruthBlockHint(null);
          setAnswerAck(t("party.joinBluff.answerRecorded"));
        }
        return;
      }
      const ready = parseBluffSocketReadyPayload(data);
      if (ready) {
        setMyConnectionId(ready.connection_id);
        sendJoinOnce();
        return;
      }
      const pres = parseBluffPresencePayload(data);
      if (pres) {
        setPlayerRoster((r) => applyPresenceToRoster(r, pres));
        return;
      }
      const game = parseBluffGamePayload(data);
      if (game) {
        setServerGame(game);
        return;
      }
      if (data && typeof data === "object" && (data as { type?: unknown }).type === "pong") {
        return;
      }
      const parsed = parseAnswerBluffIncoming(raw);
      if (!parsed) return;
      setPhase(parsed.phase);
      setRound(parsed.round);
      if (parsed.question != null) setQuestion(parsed.question);
      if (parsed.answer != null) setAnswer(parsed.answer);
      if (parsed.phase !== "vote") setVoteSentForRound(null);
    };

    ws.onerror = () => {
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_WS_ERROR, { stage: "socket" });
      setConnectError(t("party.joinBluff.errorConnect"));
    };

    ws.onclose = () => {
      clearBluffWsPing();
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_WS_CLOSED, {
        join_had_been_sent: joinSentRef.current,
      });
      if (joinFallbackRef.current) {
        clearTimeout(joinFallbackRef.current);
        joinFallbackRef.current = null;
      }
      const userStopped = guestStopReconnectRef.current;
      guestStopReconnectRef.current = false;
      setConnected(false);
      wsRef.current = null;
      joinSentRef.current = false;
      setJoinSentEpoch(0);
      setMyConnectionId(null);
      setPlayerRoster({});
      setServerGame(null);
      setAnswerSubmitLocked(false);
      if (
        !userStopped &&
        !missing &&
        displayName.trim() &&
        guestHadJoinedSessionRef.current
      ) {
        let attempt = 0;
        const schedule = () => {
          if (guestStopReconnectRef.current || guestReconnectTimerRef.current != null) return;
          const delay = Math.min(30_000, 700 * Math.pow(2, Math.min(attempt, 6)));
          attempt += 1;
          guestReconnectTimerRef.current = window.setTimeout(() => {
            guestReconnectTimerRef.current = null;
            connect();
          }, delay);
        };
        schedule();
      }
    };
  }, [clearBluffWsPing, disconnect, displayName, missing, t, wsPath]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      if (guestStopReconnectRef.current || missing || !displayName.trim()) return;
      if (!guestHadJoinedSessionRef.current) return;
      const w = wsRef.current;
      if (!w || w.readyState !== WebSocket.OPEN) {
        if (guestReconnectTimerRef.current != null) {
          window.clearTimeout(guestReconnectTimerRef.current);
          guestReconnectTimerRef.current = null;
        }
        connect();
      } else {
        try {
          w.send(stringifyBluffWsPing());
        } catch {
          /* ignore */
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [connect, missing, displayName, code, wsPath]);

  const sendLegacyVote = (verdict: "truth" | "bluff") => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || round < 1) return;
    try {
      ws.send(stringifyVote(round, verdict));
      setVoteSentForRound(round);
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_VOTE_SUBMITTED, {
        mode: "legacy",
        round,
        verdict,
      });
    } catch {
      setConnectError(t("party.joinBluff.errorConnect"));
    }
  };

  const sendServerAnswer = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const text = answerDraft.trim();
    if (!text) return;
    const sg = serverGameRef.current;
    if (!sg || !bluffQaCombinedPhase(sg.phase)) return;
    const truth = resolveBluffCanonicalTruth(undefined, sg);
    if (truth && bluffSubmissionMatchesTruth(text, truth)) {
      setAnswerTruthBlockHint(t("party.joinBluff.cannotSubmitTruth"));
      return;
    }
    setAnswerTruthBlockHint(null);
    try {
      ws.send(JSON.stringify({ type: "bluff_answer", text }));
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_ANSWER_SUBMITTED, {
        round_index: sg.round_index,
        answer_length: text.length,
      });
    } catch {
      setConnectError(t("party.joinBluff.errorConnect"));
    }
  };

  const sendServerVote = (choiceId: number) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify({ type: "bluff_vote", choice_id: choiceId }));
      setVoteSubmitted(true);
      trackEvent(MixpanelEvents.PARTY_BLUFF_GUEST_VOTE_SUBMITTED, {
        mode: "choice",
        choice_id: choiceId,
      });
    } catch {
      setConnectError(t("party.joinBluff.errorConnect"));
    }
  };

  const scoreRows = serverGame
    ? Object.keys(serverGame.scores)
      .map((id) => ({
        name: serverGame.players[id] ?? id.slice(-8),
        pts: serverGame.scores[id] ?? 0,
      }))
      .sort((a, b) => b.pts - a.pts)
    : [];

  const topName = scoreRows[0]?.name;

  const countdownLabel =
    serverGame?.phase_ends_at && phaseSecondsLeft !== null
      ? phaseSecondsLeft > 0
        ? t("party.joinBluff.timerSeconds", { count: phaseSecondsLeft })
        : t("party.joinBluff.timerEnded")
      : null;

  const joinBluffTimerLive = serverGame?.phase_ends_at != null && phaseSecondsLeft != null && phaseSecondsLeft > 0;

  const votePhase = serverGame?.phase;
  const voteRoundIndex = serverGame?.round_index;
  const votePhaseEndsAt = serverGame?.phase_ends_at;
  const voteOptionsRaw = serverGame?.vote_options;

  const voteOptionsShuffled = useMemo(() => {
    if (votePhase !== "vote" || !voteOptionsRaw?.length) return [];
    const seed = bluffVoteOptionsShuffleSeed(voteRoundIndex ?? 0, votePhaseEndsAt ?? null, voteOptionsRaw);
    return shuffleBluffVoteOptionsDeterministic(voteOptionsRaw, seed);
  }, [votePhase, voteRoundIndex, votePhaseEndsAt, voteOptionsRaw]);

  return (
    <Box
      className="party-shell-root"
      dir={rtl ? "rtl" : "ltr"}
      lang={i18n.language || "en"}
      data-party-theme="midnight"
      data-party-type="rubik"
      sx={{
        minHeight: "100dvh",
        bgcolor: partyPal.bg,
        color: partyPal.ink,
        py: 2,
      }}
    >
      <Container maxWidth="sm">
        <Stack spacing={2}>
          <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.14em", color: partyPal.inkDim }}>
            {code ? `${t("party.joinBluff.title")} · ${code}` : t("party.joinBluff.title")}
          </Typography>

          {missing ? (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1.5px solid ${partyPal.hot}`,
                bgcolor: "rgba(255,61,120,0.08)",
              }}
            >
              <Typography sx={{ fontWeight: 800, color: partyPal.ink, lineHeight: 1.45 }}>{t("party.joinBluff.missingParams")}</Typography>
            </Paper>
          ) : null}

          {!missing && !connected && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1.5px solid ${partyPal.inkFaint}`,
                bgcolor: "rgba(255,255,255,0.04)",
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography className="party-display" sx={{ fontSize: "clamp(22px, 5.5vw, 28px)", fontWeight: 900, mb: 1, color: partyPal.ink }}>
                    {t("party.joinBluff.connectHowTitle")}
                  </Typography>
                  <Typography sx={{ fontSize: 16, lineHeight: 1.55, color: partyPal.inkDim, fontWeight: 600 }}>
                    {t("party.joinBluff.connectHowBody")}
                  </Typography>
                </Box>
                <TextField
                  label={t("party.joinBluff.displayNameLabel")}
                  placeholder={t("party.joinBluff.displayNamePlaceholder")}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  onKeyDown={(e) =>
                    handlePartyRosterNameEnter(e, () => {
                      if (!displayName.trim()) return;
                      connect();
                    })
                  }
                  fullWidth
                  autoComplete="nickname"
                  sx={fieldSx}
                />
                <Button
                  type="button"
                  variant="contained"
                  className="party-btn party-btn-hot"
                  disabled={!displayName.trim()}
                  onClick={connect}
                  fullWidth
                >
                  {t("party.joinBluff.connect")}
                </Button>
              </Stack>
            </Paper>
          )}

          {connected && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1.5px solid ${partyPal.inkFaint}`,
                bgcolor: "rgba(255,255,255,0.04)",
              }}
            >
              <Stack spacing={2} alignItems="stretch">
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: 13, color: partyPal.inkDim }}>{t("party.joinBluff.connected")}</Typography>
                  <Button type="button" size="small" variant="text" onClick={() => disconnect({ userLeaving: true })} sx={{ color: partyPal.hot }}>
                    {t("party.joinBluff.disconnect")}
                  </Button>
                </Stack>

                <BluffPlayersRoster
                  roster={rosterForUi}
                  title={t("party.joinBluff.playersTitle")}
                  emptyHint={t("party.joinBluff.playersEmpty")}
                  myConnectionId={myConnectionId}
                  youLabel={t("party.joinBluff.youBadge")}
                  partyPal={partyPal}
                />

                {serverGame ? (
                  <>
                    <BluffScoresStrip rows={scoreRows} title={t("party.playBluff.scoresStripTitle")} partyPal={partyPal} />

                    {serverGame.phase === "lobby" && (
                      <BluffQuestionCard
                        phaseLabel={bluffPhaseEmojiLabel(t, serverGame.phase)}
                        countdown={countdownLabel}
                        partyPal={partyPal}
                        timerLive={joinBluffTimerLive}
                      >
                        <Typography sx={{ fontSize: 17, lineHeight: 1.55, fontWeight: 700, color: partyPal.paperInk }}>{t("party.joinBluff.waitingHost")}</Typography>
                      </BluffQuestionCard>
                    )}

                    {bluffQaCombinedPhase(serverGame.phase) && (
                      <BluffQuestionCard
                        phaseLabel={bluffPhaseEmojiLabel(t, "qa")}
                        countdown={countdownLabel}
                        partyPal={partyPal}
                        timerLive={joinBluffTimerLive}
                        questionHighlight={serverGame.current_prompt ?? null}
                      >
                        <Stack spacing={2}>
                          <Stack spacing={0.75} sx={{ alignItems: "center", pt: 0.5 }}>
                            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.14em", color: partyPal.paperInk, opacity: 0.72 }}>
                              {t("party.joinBluff.questionHeading")}
                            </Typography>
                            <Typography className="party-mono" sx={{ fontSize: 12, color: partyPal.paperInk, opacity: 0.62 }}>
                              {t("party.joinBluff.round", { n: serverGame.round_index + 1 })}
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              p: 1.75,
                              borderRadius: 2,
                              border: `1px solid ${alpha(partyPal.paperInk, 0.12)}`,
                              bgcolor: alpha(partyPal.paperInk, 0.04),
                            }}
                          >
                            <Typography className="party-display" sx={{ fontSize: "clamp(20px, 4.8vw, 26px)", fontWeight: 900, mb: 1, color: partyPal.paperInk }}>
                              {t("party.playBluff.guestInstructionsTitle")}
                            </Typography>
                            <Typography sx={{ fontSize: 16, lineHeight: 1.55, fontWeight: 600, color: partyPal.paperInk, opacity: 0.92 }}>
                              {t("party.playBluff.guestInstructionsBody")}
                            </Typography>
                          </Box>
                          {!answerSubmitLocked ? (
                            <>
                              <Typography className="party-mono" sx={{ fontSize: 12, letterSpacing: "0.12em", color: partyPal.hot, fontWeight: 800 }}>
                                {t("party.joinBluff.typeYourAnswer")}
                              </Typography>
                              <TextField
                                value={answerDraft}
                                onChange={(e) => {
                                  setAnswerTruthBlockHint(null);
                                  setAnswerDraft(e.target.value);
                                }}
                                placeholder={t("party.joinBluff.answerPlaceholder")}
                                fullWidth
                                multiline
                                minRows={2}
                                error={Boolean(answerTruthBlockHint)}
                                helperText={answerTruthBlockHint ?? undefined}
                                sx={answerFieldSx}
                              />
                              <Button
                                type="button"
                                variant="contained"
                                className="party-btn party-btn-hot"
                                onClick={sendServerAnswer}
                                disabled={!answerDraft.trim()}
                              >
                                {t("party.joinBluff.sendAnswer")}
                              </Button>
                            </>
                          ) : (
                            <Typography sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.45, color: partyPal.paperInk }}>
                              {t("party.playBluff.answerLockedIn")}
                            </Typography>
                          )}
                          {answerAck ? (
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: partyPal.paperInk, opacity: 0.8 }}>{answerAck}</Typography>
                          ) : null}
                        </Stack>
                      </BluffQuestionCard>
                    )}

                    {serverGame.phase === "vote" && (
                      <BluffQuestionCard
                        phaseLabel={bluffPhaseEmojiLabel(t, serverGame.phase)}
                        countdown={countdownLabel}
                        partyPal={partyPal}
                        timerLive={joinBluffTimerLive}
                      >
                        <Stack spacing={2} alignItems="stretch" sx={{ width: "100%" }}>
                          <Typography className="party-display" sx={{ fontSize: "clamp(18px, 4.2vw, 22px)", fontWeight: 900, textAlign: "center", lineHeight: 1.35, color: partyPal.paperInk }}>
                            {t("party.joinBluff.votePickTruth")}
                          </Typography>
                          {voteSubmitted ? (
                            <Typography
                              className="party-mono"
                              sx={{ color: partyPal.violet, textAlign: "center", fontWeight: 800, fontSize: 15, letterSpacing: "0.06em" }}
                            >
                              ✓ {t("party.joinBluff.voteSent")}
                            </Typography>
                          ) : (
                            <Stack spacing={1.25}>
                              {(voteOptionsShuffled.length ? voteOptionsShuffled : (serverGame.vote_options ?? [])).map((opt) => (
                                <Button
                                  key={opt.id}
                                  type="button"
                                  fullWidth
                                  variant="outlined"
                                  className="party-btn party-btn-paper"
                                  onClick={() => sendServerVote(opt.id)}
                                  sx={{
                                    ...bluffVoteOptionButtonSx(partyPal),
                                    py: 1.5,
                                    textAlign: "center",
                                    whiteSpace: "normal",
                                    lineHeight: 1.35,
                                    textTransform: "none",
                                    fontSize: 16,
                                  }}
                                >
                                  {opt.text}
                                </Button>
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </BluffQuestionCard>
                    )}

                    {(serverGame.phase === "leaderboard" || serverGame.phase === "game_over") && scoreRows.length > 0 && (
                      <BluffQuestionCard
                        phaseLabel={bluffPhaseEmojiLabel(t, serverGame.phase)}
                        countdown={countdownLabel}
                        partyPal={partyPal}
                        timerLive={joinBluffTimerLive}
                      >
                        <BluffLeaderboardList
                          rows={scoreRows}
                          partyPal={partyPal}
                          headline={serverGame.phase === "game_over" ? t("party.joinBluff.finalScores") : t("party.joinBluff.roundScores")}
                          winnerLine={
                            serverGame.phase === "game_over" && topName ? t("party.joinBluff.winner", { name: topName }) : null
                          }
                        />
                        {serverGame.phase === "game_over" ? (
                          <PartyShareResultButton
                            className="party-btn party-btn-paper"
                            sx={{ width: "100%", mt: 1.5 }}
                            model={{
                              shellId: "bluff",
                              modeTitle: shareModeTitle,
                              resultTitle: t("party.joinBluff.finalScores"),
                              hero: topName ?? "",
                              sub: topName ? t("party.joinBluff.winner", { name: topName }) : undefined,
                              standings: scoreRows.slice(0, 4).map((r) => ({ name: r.name, value: `${r.pts}` })),
                            }}
                          />
                        ) : null}
                      </BluffQuestionCard>
                    )}
                  </>
                ) : (
                  <>
                    {round > 0 && (
                      <Typography className="party-mono" sx={{ fontSize: 12, color: partyPal.inkDim }}>
                        {t("party.joinBluff.round", { n: round })}
                      </Typography>
                    )}

                    {phase === "idle" && (
                      <Typography sx={{ color: partyPal.inkDim, fontSize: 15, lineHeight: 1.5 }}>{t("party.joinBluff.waitingHost")}</Typography>
                    )}

                    {(phase === "question" || phase === "answer") && question && (
                      <Stack spacing={1.5}>
                        <Box>
                          <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.12em", color: partyPal.hot, mb: 1 }}>
                            {t("party.joinBluff.questionHeading")}
                          </Typography>
                          <Typography className="party-display" sx={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
                            {question}
                          </Typography>
                        </Box>
                        {phase === "answer" ? (
                          <Box>
                            <Typography className="party-mono" sx={{ fontSize: 11, letterSpacing: "0.12em", color: partyPal.hot, mb: 1 }}>
                              {t("party.joinBluff.answerHeading")}
                            </Typography>
                            <Typography className="party-display" sx={{ fontSize: "clamp(20px, 4.5vw, 26px)", lineHeight: 1.3 }}>
                              {answer || "—"}
                            </Typography>
                          </Box>
                        ) : null}
                      </Stack>
                    )}

                    {phase === "vote" && (
                      <Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, textAlign: "center", lineHeight: 1.35 }}>
                          {t("party.joinBluff.voteHeading")}
                        </Typography>
                        {voteSentForRound === round ? (
                          <Typography sx={{ color: partyPal.inkDim }}>{t("party.joinBluff.voteSent")}</Typography>
                        ) : (
                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }}>
                            <Button
                              type="button"
                              fullWidth
                              variant="contained"
                              className="party-btn party-btn-paper"
                              onClick={() => sendLegacyVote("truth")}
                              sx={{ py: 2, fontSize: 16 }}
                            >
                              {t("party.joinBluff.voteTruth")}
                            </Button>
                            <Button
                              type="button"
                              fullWidth
                              variant="contained"
                              className="party-btn party-btn-hot"
                              onClick={() => sendLegacyVote("bluff")}
                              sx={{ py: 2, fontSize: 16 }}
                            >
                              {t("party.joinBluff.voteBluff")}
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          )}

          {connectError && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 3,
                border: `1.5px solid ${partyPal.hot}`,
                bgcolor: "rgba(0,0,0,0.28)",
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                <Typography sx={{ fontWeight: 800, color: partyPal.ink, lineHeight: 1.45, flex: 1 }}>{connectError}</Typography>
                <Button type="button" size="small" onClick={() => setConnectError(null)} sx={{ color: partyPal.inkDim, minWidth: "auto", px: 0.5 }}>
                  ×
                </Button>
              </Stack>
            </Paper>
          )}

        </Stack>
      </Container>
    </Box>
  );
}
